/**
 * Manages syncedObjects.
 */

import { ApiSync, Syncable, SyncedObject } from './ApiSync';
import ImsObjectCollection from '../collections/ims_object_collection.service';
import { EventRegistry } from '../events/event_registry';
import { StateMachine } from '../state_machine/StateMachine';
import { StoreAndForwardJobStore } from 'app/shared/store_and_forward/StoreAndForwardJobStore';

interface ApiSyncFactoryFn {
    <T extends Syncable<T>>(
        collection: ImsObjectCollection,
        syncedObject: SyncedObject<T>,
        syncWrite: boolean,
    ): ApiSync<T>;
}

// Our production api sync factory
function defaultApiSyncFactory<T extends Syncable<T>>(
    collection,
    syncedObject,
    syncWrite,
): ApiSync<T> {
    return new ApiSync<T>(collection, syncedObject, syncWrite);
}

const STATE_SYNCING = 'state_syncing';
const STATE_NOT_SYNCING = 'state_not_syncing';

const EVENT_SYNC_DETECTED = 'event_sync_detected';
const EVENT_SYNC_NOT_DETECTED = 'event_sync_not_detected';

function isSyncing(apiSyncSet: Set<any>): boolean {
    // If any of the api sync objects are syncing, return true
    for (let apiSync of apiSyncSet) {
        if (apiSync.isSyncing()) {
            return true; // early return
        }
    }
    return false;
}

export interface SyncContext<T extends Syncable<T>> {
    apiSync: ApiSync<T>;
    destroy: () => void;
}

/**
 * An abstraction for managing the 'background' processes that run against
 * SyncedObjects. This service knows if the application is currently syncing or not.
 * All sync processes are started from this service.
 *
 * At the moment it's very specific about types but this can be moved over to the ims typed system
 * later if we end up wanting to sync different objects.
 */
export class ApiSyncService {
    private _apiSyncRegister: Set<any>;
    private _apiSyncFactory: ApiSyncFactoryFn;
    private _isSyncingChangedEventRegistry: EventRegistry;
    private _isSyncingFSM: StateMachine;
    private _storeAndForwardJobStore: StoreAndForwardJobStore;

    constructor(
        storeAndForwardJobStore: StoreAndForwardJobStore,
        apiSyncFactory: ApiSyncFactoryFn = null,
    ) {
        if (!storeAndForwardJobStore) {
            throw new Error('storeAndForwardJobStore must be truthy');
        }
        this._storeAndForwardJobStore = storeAndForwardJobStore;

        // Create a listener, dont worry about cleaning it up since ApiSyncService
        // lives forever
        this._storeAndForwardJobStore.addOnChangeListener(() => {
            this._checkSyncing();
        });

        this._isSyncingChangedEventRegistry = new EventRegistry();
        // initialise the changed event registry to false - there will be
        // be no listeners assigned at this point so this will have no other effect
        this._isSyncingChangedEventRegistry.notifyListeners(false);

        this._apiSyncRegister = new Set();
        if (!apiSyncFactory) {
            apiSyncFactory = defaultApiSyncFactory;
        }
        this._apiSyncFactory = apiSyncFactory;

        // Emits events when the syncing state changes.
        // Could make this into its 'boolean changed' state machine actually, it could
        // be handy elsewhere.
        this._isSyncingFSM = new StateMachine(
            [
                {
                    name: STATE_NOT_SYNCING,
                },
                {
                    name: STATE_SYNCING,
                    onBeforeEnterActions: [
                        () => {
                            this._isSyncingChangedEventRegistry.notifyListeners(true);
                        },
                    ],
                    onBeforeExitActions: [
                        () => {
                            this._isSyncingChangedEventRegistry.notifyListeners(false);
                        },
                    ],
                },
            ],
            [
                {
                    eventName: EVENT_SYNC_DETECTED,
                    sourceStateName: STATE_NOT_SYNCING,
                    destStateName: STATE_SYNCING,
                },
                {
                    eventName: EVENT_SYNC_NOT_DETECTED,
                    sourceStateName: STATE_SYNCING,
                    destStateName: STATE_NOT_SYNCING,
                },
                {
                    eventName: EVENT_SYNC_DETECTED,
                    sourceStateName: STATE_SYNCING,
                },
                {
                    eventName: EVENT_SYNC_NOT_DETECTED,
                    sourceStateName: STATE_NOT_SYNCING,
                },
            ],
        );
    }

    isSyncing(): boolean {
        return this._isSyncingFSM.currentStateName === STATE_SYNCING;
    }

    _checkSyncing() {
        const syncing =
            isSyncing(this._apiSyncRegister) || this._storeAndForwardJobStore.getJobCount() > 0;
        if (syncing) {
            this._isSyncingFSM.handleEvent(EVENT_SYNC_DETECTED);
        } else {
            this._isSyncingFSM.handleEvent(EVENT_SYNC_NOT_DETECTED);
        }
    }

    addIsSyncingChangedListener(callback: (boolean) => void): () => void {
        return this._isSyncingChangedEventRegistry.addEventListener(callback);
    }

    /**
     * For debugging. Allows us to check whether the # of processes
     * is getting out of control using the handy console command:
     * window.services.apiSyncService.numApiSyncProcesses
     * @returns {number}
     */
    get numApiSyncProcesses(): number {
        return this._apiSyncRegister.size;
    }

    /**
     * Starts a scheduled sync process with the API.
     * Handles read and write.
     * It relies on the SyncedObject to handle when to accept changes / detect
     * conflicts etc.
     *
     * @param {SyncedObject<T extends Syncable<T>>} syncedObject
     * @param syncWrite: boolean if true, will write changes to the API
     * @returns {() => void}
     */
    startSync<T extends Syncable<T>>(
        syncedObject: SyncedObject<T>,
        syncWrite: boolean,
    ): SyncContext<T> {
        const collectionLookup = {};
        const imsType = syncedObject.getCurrentObj().imsType;
        const collection: ImsObjectCollection = collectionLookup[imsType];
        if (!collection) {
            throw new Error('No api sync service collection for ims type ' + imsType);
        }

        const apiSync = this._apiSyncFactory<T>(collection, syncedObject, syncWrite);
        const rmStateChangeListener = apiSync.addOnStateChangeCallback(() => {
            this._checkSyncing();
        });
        this._apiSyncRegister.add(apiSync);

        return {
            apiSync,
            destroy: () => {
                rmStateChangeListener();
                apiSync.destroy();
                this._apiSyncRegister.delete(apiSync);
                // Just removed a sync context that may effect the syncing state.
                // Update accordingly...
                this._checkSyncing();
            },
        };
    }
}
